import React, { useState } from 'react';
import {
  Box, Typography, Stack, Paper, Button, Chip, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, Alert, Snackbar,
  CircularProgress, Divider, Avatar, Tooltip, IconButton,
} from '@mui/material';
import EventIcon from '@mui/icons-material/Event';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import VideocamIcon from '@mui/icons-material/Videocam';
import PeopleIcon from '@mui/icons-material/People';
import { useQuery, useMutation } from '@apollo/client';
import {
  MY_MEETINGS_QUERY, USERS_QUERY,
  CREATE_MEETING_MUTATION, RESPOND_TO_MEETING_MUTATION, CANCEL_MEETING_MUTATION,
} from '../graphql/queries';
import { useAuth } from '../context/AuthContext';
import { fmtDate } from '../utils/dateUtils';

const MEETING_TYPE_CONFIG = {
  family_meeting: { label: 'Family Meeting', color: 'primary',   icon: <PeopleIcon fontSize="small" /> },
  video_call:     { label: 'Video Call',      color: 'info',      icon: <VideocamIcon fontSize="small" /> },
  in_person:      { label: 'In-Person',       color: 'success',   icon: <LocationOnIcon fontSize="small" /> },
};

const STATUS_CONFIG = {
  pending:   { label: 'Pending',   color: 'warning' },
  confirmed: { label: 'Confirmed', color: 'success' },
  cancelled: { label: 'Cancelled', color: 'error' },
};

const RESPONSE_CONFIG = {
  accepted: { label: 'Accepted', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  declined: { label: 'Declined', color: 'error',   icon: <CancelIcon fontSize="small" /> },
  pending:  { label: 'Pending',  color: 'warning',  icon: <AccessTimeIcon fontSize="small" /> },
};

function MeetingCard({ meeting, currentUserId, onRespond, onCancel }) {
  const typeCfg    = MEETING_TYPE_CONFIG[meeting.meetingType] || MEETING_TYPE_CONFIG.family_meeting;
  const statusCfg  = STATUS_CONFIG[meeting.status] || STATUS_CONFIG.pending;
  const respCfg    = RESPONSE_CONFIG[meeting.myResponse || 'pending'];
  const isOrganiser = meeting.organiserId === currentUserId;
  const isPending   = meeting.myResponse === 'pending' && meeting.status !== 'cancelled';

  return (
    <Paper elevation={1} sx={{ p: 2.5, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
        <Box flex={1}>
          <Typography variant="h6" fontWeight={700}>{meeting.title}</Typography>
          <Stack direction="row" spacing={1} mt={0.5} flexWrap="wrap">
            <Chip label={typeCfg.label} color={typeCfg.color} size="small" icon={typeCfg.icon} />
            <Chip label={statusCfg.label} color={statusCfg.color} size="small" />
            {!isOrganiser && <Chip label={`My response: ${respCfg.label}`} color={respCfg.color} size="small" icon={respCfg.icon} />}
            {isOrganiser && <Chip label="You organised" color="default" size="small" variant="outlined" />}
          </Stack>
        </Box>
      </Stack>

      {meeting.description && (
        <Typography variant="body2" color="text.secondary" mb={1.5}>{meeting.description}</Typography>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={1.5}>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <EventIcon fontSize="small" color="action" />
          <Typography variant="body2" fontWeight={600}>{fmtDate(meeting.proposedAt, 'datetime')}</Typography>
        </Stack>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <AccessTimeIcon fontSize="small" color="action" />
          <Typography variant="body2" color="text.secondary">{meeting.durationMinutes} min</Typography>
        </Stack>
        {meeting.location && (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <LocationOnIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">{meeting.location}</Typography>
          </Stack>
        )}
      </Stack>

      <Divider sx={{ my: 1 }} />

      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
          <PeopleIcon fontSize="small" color="action" />
          {meeting.participants.map(p => (
            <Tooltip key={p.id} title={`${p.user?.email} — ${p.response}`}>
              <Chip
                avatar={<Avatar sx={{ fontSize: 10 }}>{(p.user?.email || '?')[0].toUpperCase()}</Avatar>}
                label={p.response}
                color={RESPONSE_CONFIG[p.response]?.color || 'default'}
                size="small"
                variant="outlined"
              />
            </Tooltip>
          ))}
        </Stack>

        <Stack direction="row" spacing={1}>
          {isPending && (
            <>
              <Button size="small" variant="outlined" color="error" startIcon={<CancelIcon />}
                onClick={() => onRespond(meeting.id, 'declined')}>
                Decline
              </Button>
              <Button size="small" variant="contained" color="success" startIcon={<CheckCircleIcon />}
                onClick={() => onRespond(meeting.id, 'accepted')}>
                Accept
              </Button>
            </>
          )}
          {isOrganiser && meeting.status !== 'cancelled' && (
            <Button size="small" variant="outlined" color="error"
              onClick={() => onCancel(meeting.id)}>
              Cancel Meeting
            </Button>
          )}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function MeetingsPage() {
  const { user: authUser } = useAuth();
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', proposedAt: '', durationMinutes: 60,
    location: '', meetingType: 'family_meeting', participantIds: [],
  });

  const { data, loading, error, refetch } = useQuery(MY_MEETINGS_QUERY, { fetchPolicy: 'cache-and-network' });
  const { data: usersData } = useQuery(USERS_QUERY);

  const [createMeeting, { loading: creating }]     = useMutation(CREATE_MEETING_MUTATION);
  const [respondToMeeting, { loading: responding }] = useMutation(RESPOND_TO_MEETING_MUTATION);
  const [cancelMeeting]                             = useMutation(CANCEL_MEETING_MUTATION);

  const showSnack = (msg, severity = 'success') => setSnack({ open: true, msg, severity });
  const fch = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const meetings = data?.myMeetings || [];
  const allUsers = (usersData?.users || []).filter(u => u.id !== authUser?.id);

  const upcoming  = meetings.filter(m => m.status !== 'cancelled' && new Date(m.proposedAt) >= new Date());
  const past      = meetings.filter(m => m.status !== 'cancelled' && new Date(m.proposedAt) < new Date());
  const cancelled = meetings.filter(m => m.status === 'cancelled');

  const handleCreate = async () => {
    if (!form.title.trim() || !form.proposedAt || !form.participantIds.length) return;
    try {
      await createMeeting({
        variables: {
          input: {
            title:           form.title.trim(),
            description:     form.description.trim() || undefined,
            proposedAt:      new Date(form.proposedAt).toISOString(),
            durationMinutes: parseInt(form.durationMinutes) || 60,
            location:        form.location.trim() || undefined,
            meetingType:     form.meetingType,
            participantIds:  form.participantIds,
          },
        },
      });
      await refetch();
      showSnack('Meeting scheduled and invitations sent!');
      setCreateOpen(false);
      setForm({ title: '', description: '', proposedAt: '', durationMinutes: 60, location: '', meetingType: 'family_meeting', participantIds: [] });
    } catch (e) { showSnack(e.message, 'error'); }
  };

  const handleRespond = async (meetingId, response) => {
    try {
      await respondToMeeting({ variables: { meetingId, response } });
      await refetch();
      showSnack(response === 'accepted' ? 'You accepted the meeting!' : 'You declined the meeting.');
    } catch (e) { showSnack(e.message, 'error'); }
  };

  const handleCancel = async (meetingId) => {
    try {
      await cancelMeeting({ variables: { meetingId } });
      await refetch();
      showSnack('Meeting cancelled.');
    } catch (e) { showSnack(e.message, 'error'); }
  };

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="warning">
          {error.message.includes('Forbidden')
            ? 'You do not have permission to schedule meetings. Please contact the administrator.'
            : error.message}
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Box>
          <Typography variant="h4" fontWeight={700}>Meetings</Typography>
          <Typography variant="body2" color="text.secondary">
            Schedule and coordinate family meetings
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateOpen(true)}>
          Schedule Meeting
        </Button>
      </Stack>

      {loading && !meetings.length && (
        <Stack alignItems="center" py={6}><CircularProgress /></Stack>
      )}

      {!loading && meetings.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 3, border: '2px dashed #e0e0e0' }}>
          <EventIcon sx={{ fontSize: 48, color: '#bdbdbd', mb: 2 }} />
          <Typography color="text.secondary">No meetings yet. Schedule a family meeting!</Typography>
        </Paper>
      )}

      {upcoming.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="primary.dark">
            Upcoming ({upcoming.length})
          </Typography>
          <Stack spacing={2}>
            {upcoming.map(m => (
              <MeetingCard key={m.id} meeting={m} currentUserId={authUser?.id}
                onRespond={handleRespond} onCancel={handleCancel} />
            ))}
          </Stack>
        </Box>
      )}

      {past.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="text.secondary">
            Past Meetings ({past.length})
          </Typography>
          <Stack spacing={2}>
            {past.map(m => (
              <MeetingCard key={m.id} meeting={m} currentUserId={authUser?.id}
                onRespond={handleRespond} onCancel={handleCancel} />
            ))}
          </Stack>
        </Box>
      )}

      {cancelled.length > 0 && (
        <Box mb={3}>
          <Typography variant="h6" fontWeight={700} mb={1.5} color="error.dark">
            Cancelled ({cancelled.length})
          </Typography>
          <Stack spacing={2}>
            {cancelled.map(m => (
              <MeetingCard key={m.id} meeting={m} currentUserId={authUser?.id}
                onRespond={handleRespond} onCancel={handleCancel} />
            ))}
          </Stack>
        </Box>
      )}

      {/* Create Meeting Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Schedule a Meeting</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField label="Meeting Title" value={form.title} onChange={fch('title')} fullWidth size="small" required />
            <TextField label="Description" value={form.description} onChange={fch('description')}
              fullWidth size="small" multiline rows={2} placeholder="What will be discussed?" />
            <TextField
              label="Date & Time"
              type="datetime-local"
              value={form.proposedAt}
              onChange={fch('proposedAt')}
              fullWidth size="small" required
              InputLabelProps={{ shrink: true }}
            />
            <Stack direction="row" spacing={2}>
              <TextField label="Duration (min)" value={form.durationMinutes} onChange={fch('durationMinutes')}
                fullWidth size="small" type="number" inputProps={{ min: 15, step: 15 }} />
              <TextField label="Meeting Type" value={form.meetingType} onChange={fch('meetingType')}
                fullWidth size="small" select>
                {Object.entries(MEETING_TYPE_CONFIG).map(([val, cfg]) => (
                  <MenuItem key={val} value={val}>{cfg.label}</MenuItem>
                ))}
              </TextField>
            </Stack>
            <TextField label="Location / Link" value={form.location} onChange={fch('location')}
              fullWidth size="small" placeholder="e.g. Chennai or https://meet.google.com/..." />
            <TextField
              label="Invite Participants"
              value={form.participantIds}
              onChange={e => setForm(p => ({ ...p, participantIds: e.target.value }))}
              fullWidth size="small" select required
              SelectProps={{ multiple: true }}
              helperText="Hold Ctrl/Cmd to select multiple">
              {allUsers.map(u => (
                <MenuItem key={u.id} value={u.id}>
                  {u.role?.displayName ? `${u.email} (${u.role.displayName})` : u.email}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            disabled={creating || !form.title.trim() || !form.proposedAt || !form.participantIds.length}>
            {creating ? <CircularProgress size={18} /> : 'Schedule Meeting'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
}
